import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import { useImmer } from 'use-immer';
import * as Yup from 'yup';
import { Button, Modal, Form, Col, Alert } from 'react-bootstrap';
import FormControls, { ConditionalRender } from '../formControls';
import { codingEventPriorityEnum, eventOriginEnum } from '../../utilities/appConstants';
import CustomerCodingEventService from '../../services/CustomerCodingEventService';

const codingEventInitValues = {
    description: '',
    eventPriorityId: codingEventPriorityEnum.medium,
    //customerPriorityId: codingEventPriorityEnum.medium,
    statusId: 50,
    caseOriginId: eventOriginEnum.CustomerCreated,
    lineOfBusinessId: 0,
    customerContactId: 0,
    accountId: 0,
    account: null,
    codingEventQueueId: null,
    patientLastName: "",
    medicalRecordNumber: "",
    dateOfVisit: "",
    episodeId: "",
    visitType: "",
    state: 100
}

const codingEventValidation = Yup.object().shape({
    lineOfBusinessId: Yup.number().min(1, 'Line of business is required.'),
    customerContactId: Yup.number().min(1, 'Customer contact must be selected'),
    accountId: Yup.number().min(1, 'Account must be selected'),
    patientLastName: Yup.string().min(2, 'Patient last name must be greater than two characters.').required('Patient last name is required.'),
    medicalRecordNumber: Yup.string().min(2, 'MRN must be greater than two characters.').required('MRN is required.'),
    dateOfVisit: Yup.string().required('Date of visit is required.'),
    visitType: Yup.string().min(2, 'Visit type is required.').required('Visit type is required.'),
});

const visitTypes = [
    { "id": "DC", "name": "Discharge" },
    { "id": "OTHER", "name": "Other" },
    { "id": "RECERT", "name": "Recertification" },
    { "id": "ROC", "name": "Resumption of Care" },
    { "id": "SOC", "name": "Start of Care" }
]

const CreateCodingEventModal = (props) => {
    const [codingEvent, setCodingEvent] = useImmer(codingEventInitValues);
    const [saveError, setSaveError] = useImmer('');
    const [customers, setCustomers] = useImmer([]);
    const [contacts, setContacts] = useImmer([]);
    const [eventPriorities, setEventPriorities] = useImmer([]);
    const [lobs, setLobs] = useImmer([]);

    useEffect(() => {
        getViewModel();
    }, []);

    const getViewModel = async () => {
        const userService = new CustomerCodingEventService();
        var callResult = await userService.getCreateViewModel();
        if (callResult.result) {
            setCustomers(callResult.model.customers);

            // if the customers ref data list only has one - just default it...
            if (callResult.model.customers.length === 1) {
                setCodingEvent(draft => {
                    draft.accountId = callResult.model.customers[0].id;
                });
            }

            setEventPriorities(callResult.model.priorities);
            setLobs(callResult.model.linesOfBusiness);
        } else {
            setSaveError("Error loading data.");
        }
    }

    const formik = useFormik({
        initialValues: codingEvent,
        validationSchema: codingEventValidation,
        enableReinitialize: true,
        onSubmit: async (values, { setSubmitting, setErrors, setStatus, resetForm }) => {
            values.description = values.patientLastName + ' MRN: ' + values.medicalRecordNumber + ' ' + values.dateOfVisit + ' ' + ' EID: ' + values.episodeId + ' - ' + values.visitType;
            var service = new CustomerCodingEventService();
            var returnVal = await service.createCodingEvent(values);
            if (!returnVal.result) {
                setSaveError('Error creating new event.');
            } else {
                props.onSaveCallback(returnVal.model.id);
            }
            setSubmitting(false);
        }
    });

    // when account id is updated...
    useEffect(() => {
        const id = formik.values.accountId;
        const account = customers.find(obj => { return obj.id === id })
        let accountId = id == 0 || id == undefined ? null : id;
        if (account != null) {
            setContacts(account.contacts);
        }
        formik.setFieldValue("account", account, false)
        formik.setFieldValue("customerContactId", 0, true)
        updateCodingEventQueue(id, formik.values.lineOfBusinessId)
    }, [formik.values.accountId])

    const updateSelectedAccount = (id) => {

        formik.setFieldValue("accountId", id, true)
    }

    

    const updateLineOfBusiness = (id) => {
        formik.setFieldValue("lineOfBusinessId", id, true)
        updateCodingEventQueue(formik.values.accountId, id)
    }

    const updateCodingEventQueue = (accountId, lineOfBusinessId) => {
        const account = customers.find(obj => { return obj.id === accountId });
        var queueId = null;
        if (lineOfBusinessId > 0 && accountId > 0) {

            if (lineOfBusinessId === 1) {
                queueId = account.homecareQueueId;
            } else if (lineOfBusinessId === 2) {
                queueId = account.hospiceQueueId;
            }

        }
        formik.setFieldValue("codingEventQueueId", queueId, false)
    }

    return (
        <>
            <Modal.Header>Create new coding event</Modal.Header>
            <Modal.Body>
                <Alert variant='danger' show={saveError}>{saveError}</Alert>
                <Form.Row>
                    <Form.Group as={Col} lg="4" md="12">
                        <FormControls.SelectBox
                            label="Account"
                            name="accountId"
                            numeric
                            formHandler={formik}
                            defaultValue={0}
                            showDefault
                            defaultLabel="Select account ..."
                            onChange={(e) => updateSelectedAccount(parseInt(e.target.value))}
                            options={customers}
                        />
                    </Form.Group>
                    <Form.Group as={Col} lg="4" md="12">
                        <FormControls.SelectBox
                            label="Line of Business"
                            name="lineOfBusinessId"
                            numeric
                            formHandler={formik}
                            disabled={formik.values.accountId === 0}
                            defaultValue={0}
                            showDefault
                            defaultLabel="Select LOB ..."
                            onChange={(e) => updateLineOfBusiness(parseInt(e.target.value))}
                            options={lobs}
                        />
                    </Form.Group>
                    <Form.Group as={Col} lg="4" md="12">
                        <FormControls.SelectBox
                            label="Account Contact"
                            name="customerContactId"
                            numeric
                            formHandler={formik}
                            defaultValue={0}
                            disabled={contacts.length === 0}
                            showDefault
                            defaultLabel="Select Contact ..."
                            onChange={(e) => {
                                const val = parseInt(e.target.value);
                                formik.setFieldValue("customerContactId", val, true)
                            }}
                            options={contacts}
                        />
                    </Form.Group>
                </Form.Row>
                <Form.Row>
                    <Form.Group as={Col} lg="4" md="6">
                        <FormControls.Textbox
                            name="patientLastName"
                            label="Patient Last Name"
                            formHandler={formik} />
                    </Form.Group>
                    <Form.Group as={Col} lg="4" md="6">
                        <FormControls.Textbox
                            name="medicalRecordNumber"
                            label="MRN"
                            formHandler={formik} />
                    </Form.Group>
                    <Form.Group as={Col} lg="4" md="6">
                        <FormControls.Textbox
                            type="date"
                            name="dateOfVisit"
                            label="Date of Visit"
                            formHandler={formik} />
                    </Form.Group>
                    <Form.Group as={Col} lg="4" md="6">
                        <FormControls.Textbox
                            name="episodeId"
                            label="Episode Id"
                            formHandler={formik} />
                    </Form.Group>
                    <Form.Group as={Col} lg="4" md="6">
                        <FormControls.SelectBox
                            label="Visit Type"
                            name="visitType"
                            numeric
                            formHandler={formik}
                            defaultValue={""}
                            disabled={contacts.length === 0}
                            showDefault
                            defaultLabel="Select Visit Type ..."
                            onChange={(e) => {
                                const val = e.target.value;
                                formik.setFieldValue("visitType", val, true)
                            }}
                            options={visitTypes}
                            />
                    </Form.Group>
                </Form.Row>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="default" onClick={() => props.closeModal()}>Cancel</Button>
                <Button variant="primary" disabled={formik.isSubmitting} onClick={() => formik.submitForm()}>Save</Button>
            </Modal.Footer>
        </>
    )
}

export default CreateCodingEventModal;